import React, {useMemo, useState} from "react";
import {
    Button, Card,
    CardContent,
    Grid,
    Typography,
    Accordion, AccordionSummary, AccordionDetails, Divider, IconButton
} from "@mui/material";
import {humanizeCargoDirection, humanizeCargoType} from "~/apps/packages/auto-visits/utils";
import {Cargo, Service} from "~/apps/packages/auto-visits/queries";
import {makeStyles} from "@mui/styles";
import {InlineBadge} from "~/apps/core/ui/InlineBadge";
import {Close} from "~/ui/icons";
import {PortTheme} from "~/ui/theme";
import {Box} from "@mui/system";
import {Tag} from "~/apps/core/ui/Tag";


export type AutoVisitCargoCardProps = {
    cargo: Pick<Cargo, "Name" | "Direction" | "StorageID" | "Quantity" | "Type"> & { ServiceList: Service[] };
    expandServices?: boolean
    editable: boolean
    disabled?: boolean
    onRemoveClick?: () => void
    onChangeServiceClick?: () => void
}


const useStyles = makeStyles<PortTheme>(theme => ({
    serviceAccordion: {
        boxShadow: "none",
        "&:before": {
            display: "none",
        },
        "&.Mui-expanded": {
            margin: 0
        },
        "& .MuiAccordionSummary-root": {
            minHeight: 32,
            height: 32,
            margin: 0,
            padding: "4px 8px",
            backgroundColor: "#6E798C12",
            borderRadius: 8,
            "& .MuiSvgIcon-root": {
                width: 16,
                height: 16,
                color: theme.palette.additionalSecondary.main,
            }
        },
        "& .MuiAccordionSummary-content": {
            margin: 0,
            display: "flex",
            alignItems: "center",
        },
        "& .MuiAccordionDetails-root": {
            padding: 0,
        }
    },
    serviceAccordionServicesButton: {
        width: 'calc(50% - 4px)',
        '@media (max-width: 360px)': {
            width: '120px'
        }
    },
    serviceAccordionEditButton: {
        width: 'calc(50% - 4px)',
        '@media (max-width: 360px)': {
            width: '140px'
        }
    }
}));


export const AutoVisitCargoCard = (props: AutoVisitCargoCardProps) => {
    const {cargo, editable, disabled, expandServices, onRemoveClick, onChangeServiceClick} = props;
    const [expandedServices, setExpandedServices] = useState(!!expandServices);
    const classes = useStyles();
    const {necessaryServices, additionalServices} = useMemo(() => {
        const necessaryServices = [], additionalServices = [];
        for (const service of cargo.ServiceList) {
            if (service.Necessary) {
                necessaryServices.push(service)
            } else {
                additionalServices.push(service)
            }
        }
        return {necessaryServices, additionalServices};
    }, [cargo.ServiceList]);

    const countServices = cargo.ServiceList.length;
    const noAdditionalServices = additionalServices.length == 0;

    const handleChangeExpandedServices = (value) => {
        setExpandedServices(value);
    }

    return (
        <Card style={{width: "100%"}}>
            <CardContent>
                <Grid container direction="column" wrap="nowrap" rowSpacing="10px">
                    <Grid item>
                        <Typography variant="cardTitle1" color="primary">
                            {editable && (
                                <IconButton onClick={onRemoveClick} disabled={disabled} size="mini" style={{float: "right"}}>
                                    <Close/>
                                </IconButton>
                            )}
                            {cargo.Name}
                        </Typography>
                    </Grid>
                    <Grid item container wrap="nowrap" spacing="8px">
                        {cargo.Direction && (
                            <Grid item>
                                <Tag>
                                    {humanizeCargoDirection(cargo.Direction)}
                                </Tag>
                            </Grid>
                        )}
                        {cargo.Type && (
                            <Grid item>
                                <Tag>
                                    {humanizeCargoType(cargo.Type)}
                                </Tag>
                            </Grid>
                        )}
                    </Grid>
                    <Grid item>
                        <Accordion
                            className={classes.serviceAccordion}
                            expanded={expandedServices}
                            disabled={disabled}
                            onChange={(_, v) => handleChangeExpandedServices(v)}
                        >
                            <Grid container wrap="nowrap" direction="row" columnSpacing="8px">
                                <Grid item flexGrow={1} className={classes.serviceAccordionServicesButton}>
                                    <AccordionSummary>
                                        <Typography color="primary" variant="inputText2" align="left" display="block" width="100%">
                                            Услуги
                                        </Typography>
                                        <Box ml={1} mr={1}>
                                            <InlineBadge
                                                badgeContent={countServices}
                                                showZero
                                                color={noAdditionalServices ? "additionalSecondary" : "additionalPrimary"}
                                            />
                                        </Box>
                                    </AccordionSummary>
                                </Grid>
                                {editable && (
                                    <Grid item flexGrow={1} className={classes.serviceAccordionEditButton}>
                                        <Button
                                            size="small"
                                            color="additionalPrimary"
                                            fullWidth
                                            onClick={onChangeServiceClick}
                                            disabled={disabled}
                                        >
                                            {additionalServices.length > 0 ? "Изменить услуги" : "Добавить услугу"}

                                        </Button>
                                    </Grid>
                                )}
                            </Grid>
                            <AccordionDetails>
                                <Grid container direction="column" rowSpacing="10px" marginTop="5px">
                                    {necessaryServices.length > 0 && (
                                        <Grid item>
                                            <Typography variant="inputTitle2" color="additionalSecondary.main"
                                                        marginBottom="4px">
                                                Обязательные услуги
                                            </Typography>
                                            <Grid container direction="column" rowSpacing={1} wrap="nowrap">
                                                {necessaryServices.map(service => (
                                                    <Grid item key={service.ID}>
                                                        <Typography variant="inputText2" color="primary">
                                                            {service.Name}
                                                        </Typography>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </Grid>
                                    )}
                                    {necessaryServices.length > 0 && additionalServices.length > 0 && (
                                        <Grid item>
                                            <Divider/>
                                        </Grid>
                                    )}
                                    {additionalServices.length > 0 && (
                                        <Grid item>
                                            <Typography variant="inputTitle2" color="additionalSecondary.main"
                                                        marginBottom="4px">
                                                Дополнительные услуги
                                            </Typography>
                                            <Grid container direction="column" rowSpacing={1} wrap="nowrap">
                                                {additionalServices.map(service => (
                                                    <Grid item key={service.ID}>
                                                        <Typography variant="inputText2" color="primary">
                                                            {service.Name}
                                                        </Typography>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </Grid>
                                    )}
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}
